/**
 * Admin — API Calls
 *
 * Every call to the public video API (/api/v1/*), newest first: who made it, with
 * which token, what they sent, what came back and how long it took. Rows are kept
 * 90 days. Click a row for the full record, inputs included.
 */

import { createFileRoute } from '@tanstack/react-router';
import { Fragment, useCallback, useEffect, useState, type KeyboardEvent } from 'react';
import { RefreshCw } from 'lucide-react';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';

export const Route = createFileRoute('/admin/api-calls')({
  component: AdminApiCalls,
});

interface ApiCall {
  id: string;
  createdAt: string;
  userId: string | null;
  email: string | null;
  tokenId: string | null;
  tokenName: string | null;
  source: string | null;
  method: string;
  route: string | null;
  path: string;
  videoId: string | null;
  status: number;
  error: string | null;
  durationMs: number;
  aborted: boolean;
  inputs: unknown;
  idempotencyKey: string | null;
  requestId: string | null;
  ip: string | null;
  userAgent: string | null;
}

interface Filters {
  email: string;
  videoId: string;
  organizationId: string;
  errorsOnly: boolean;
}

function errorText(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { error?: string; message?: string } }; message?: string };
  return e?.response?.data?.error || e?.response?.data?.message || e?.message || fallback;
}

function queryFor(f: Filters, before?: string): Record<string, string> {
  const q: Record<string, string> = {};
  if (f.email.trim()) q.email = f.email.trim();
  if (f.videoId.trim()) q.videoId = f.videoId.trim();
  if (f.organizationId.trim()) q.organizationId = f.organizationId.trim();
  if (f.errorsOnly) q.status = 'errors';
  if (before) q.before = before;
  return q;
}

const NO_FILTERS: Filters = { email: '', videoId: '', organizationId: '', errorsOnly: false };

function AdminApiCalls() {
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [applied, setApplied] = useState<Filters>(NO_FILTERS);
  const [calls, setCalls] = useState<ApiCall[]>([]);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (f: Filters, before?: string) => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get('/admin/api-calls', { params: queryFor(f, before) });
      setCalls((prev) => (before ? [...prev, ...r.data.calls] : r.data.calls));
      setNextBefore(r.data.nextBefore);
    } catch (err) {
      setError(errorText(err, 'Could not load API calls'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(NO_FILTERS); }, [load]);

  const refresh = () => { setExpanded(null); setApplied(filters); load(filters); };
  const set = (patch: Partial<Filters>) => setFilters((f) => ({ ...f, ...patch }));
  const onEnter = (e: KeyboardEvent) => { if (e.key === 'Enter') refresh(); };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">API Calls</h1>
          <p className="text-muted-foreground">
            Every call to the public video API (/api/v1) — who, which token, what was sent, what came back. Kept 90 days.
          </p>
        </div>
        <Button variant="outline" onClick={refresh} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[220px] flex-1">
              <Label>User email</Label>
              <Input value={filters.email} onChange={(e) => set({ email: e.target.value })} onKeyDown={onEnter} placeholder="someone@kubicle.com" />
            </div>
            <div className="min-w-[220px] flex-1">
              <Label>Video id</Label>
              <Input value={filters.videoId} onChange={(e) => set({ videoId: e.target.value })} onKeyDown={onEnter} className="font-mono" />
            </div>
            <div className="min-w-[220px] flex-1">
              <Label>Organization id</Label>
              <Input value={filters.organizationId} onChange={(e) => set({ organizationId: e.target.value })} onKeyDown={onEnter} className="font-mono" />
            </div>
            <label className="flex items-center gap-2 text-sm h-10">
              <input type="checkbox" checked={filters.errorsOnly} onChange={(e) => set({ errorsOnly: e.target.checked })} />
              Errors only
            </label>
            <Button onClick={refresh} disabled={loading}>Apply</Button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="rounded border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
      )}

      <Card>
        <CardContent className="pt-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Call</TableHead>
                <TableHead>Video</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">ms</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {calls.map((c) => (
                <Fragment key={c.id}>
                  <TableRow className="cursor-pointer" onClick={() => setExpanded((id) => (id === c.id ? null : c.id))}>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{new Date(c.createdAt).toLocaleString()}</TableCell>
                    <TableCell className="text-xs">{c.email ?? '—'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{c.source ?? '—'}</TableCell>
                    <TableCell className="font-mono text-xs">{c.method} {c.route ?? c.path}</TableCell>
                    <TableCell className="font-mono text-xs" title={c.videoId ?? undefined}>{c.videoId ? c.videoId.slice(0, 8) : '—'}</TableCell>
                    <TableCell className="text-xs">
                      <Badge variant={c.status >= 400 ? 'destructive' : 'outline'}>{c.status}</Badge>
                      {c.aborted && <Badge variant="secondary" className="ml-1">aborted</Badge>}
                      {c.error && <span className="ml-2 text-destructive">{c.error}</span>}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-xs">{c.durationMs}</TableCell>
                  </TableRow>
                  {expanded === c.id && (
                    <TableRow>
                      <TableCell colSpan={7} className="bg-muted/40">
                        <pre className="max-h-[480px] overflow-auto text-xs whitespace-pre-wrap break-all">{JSON.stringify(c, null, 2)}</pre>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
              {!loading && calls.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">
                    No calls match.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {nextBefore && (
            <div className="flex justify-center mt-4">
              <Button variant="outline" onClick={() => load(applied, nextBefore)} disabled={loading}>
                {loading ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
